import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Countdown } from 'components/common/Countdown';
import { RequestEntry } from 'components/request/RequestEntry';
import { SearchTrack } from 'components/request/SearchTrack';
import { MediaPlayer } from 'components/request/mediaPlayer';
import { SpotifyImporter } from 'components/request/spotify/SpotifyImporter';
import { useFirebase } from 'contexts/FirebaseContext';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { AnimatePresence, motion } from 'framer-motion';
import { useRequests } from 'hooks/useRequests';
import { useUser } from 'hooks/useUser';
import { EventOccasion } from 'models/EventOccasion';
import { Track } from 'models/Track';
import { TrackRequest, TrackRequesterPriorityValue } from 'models/TrackRequest';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dateExpired } from 'utils/dateExpired';

type RequestSort = 'date' | 'mine' | 'votes';

export function EventRequests() {
  const { t } = useTranslation();
  const [event, setEvent] = useState<EventOccasion>();
  const [isOpenSearchResults, setIsOpenSearchResult] = useState(false);
  const [previewTrack, setPreviewTrack] = useState<Track | null>(null);
  const [requestSort, setRequestSort] = useState<RequestSort>('date');
  const auth = getAuth();
  const { userId, eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    requests,
    fetchRequests,
    addAnonymousRequests,
    removeAnonymousRequest,
    updateRequestMessage,
    updateRequestPriority,
  } = useRequests(userId);
  const { db } = useFirebase();
  const { setRequesterId, user, setUser, requesterId } = useUser();
  const vp = searchParams.get('vp');
  const vip = (vp && event?.vipKey && event?.vipKey === vp) || false;

  const filteredRequests = requests;

  const getRequestDate = (request: TrackRequest) =>
    request.date ? new Date(request.date).getTime() : 0;

  const getVoteCount = (request: TrackRequest) => request.requesters?.length ?? 0;

  const isMyRequest = (request: TrackRequest) =>
    request.requesters?.some((requester) => requester.id === requesterId) ?? false;

  const PRIORITY_RANK: Record<TrackRequesterPriorityValue, number> = {
    [TrackRequesterPriorityValue.High]: 0,
    [TrackRequesterPriorityValue.Normal]: 1,
    [TrackRequesterPriorityValue.Low]: 2,
  };

  const getMyPriority = (request: TrackRequest): TrackRequesterPriorityValue =>
    request.priorities?.find((priority) => priority.id === requesterId)?.value ??
    TrackRequesterPriorityValue.Normal;

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    switch (requestSort) {
      case 'mine': {
        const mineA = isMyRequest(a);
        const mineB = isMyRequest(b);

        if (mineA !== mineB) {
          return Number(mineB) - Number(mineA);
        }

        if (mineA && mineB) {
          const rankDiff = PRIORITY_RANK[getMyPriority(a)] - PRIORITY_RANK[getMyPriority(b)];
          if (rankDiff !== 0) {
            return rankDiff;
          }
        }

        return getRequestDate(b) - getRequestDate(a);
      }

      case 'votes': {
        const votesDiff = getVoteCount(b) - getVoteCount(a);
        if (votesDiff !== 0) return votesDiff;
        return getRequestDate(b) - getRequestDate(a);
      }

      case 'date':
      default:
        return getRequestDate(b) - getRequestDate(a);
    }
  });

  const canRequest =
    !dateExpired(event?.expireDate) && !event?.disableRequests && user?.uid != null;

  const handleRequest = async (request: TrackRequest) => {
    if (eventId) {
      await addAnonymousRequests(eventId, [request]);
    }
  };

  const handleRemoveRequest = async (request: TrackRequest) => {
    if (eventId) {
      await removeAnonymousRequest(eventId, request);
    }
  };

  const handlePreview = (track: Track) => {
    setPreviewTrack(track);
  };

  const handleClosePreview = () => {
    setPreviewTrack(null);
  };

  const handleChangeRequestMessage = (message: string, request: TrackRequest) => {
    if (eventId) {
      updateRequestMessage(message, eventId, request);
    }
  };

  const handleChangeRequestPriority = async (
    value: TrackRequesterPriorityValue,
    request: TrackRequest
  ) => {
    if (eventId) {
      await updateRequestPriority(value, eventId, request);
    }
  };

  const navigate = useNavigate();

  const loadEvent = async () => {
    if (eventId && userId) {
      const eventRef = doc(db, `/user/${userId}/events`, eventId);
      const eventSnapshot = await getDoc(eventRef);

      if (eventSnapshot.exists()) {
        const eventData = { ...eventSnapshot.data(), id: eventId } as EventOccasion;
        setEvent(eventData);

        if (eventData.isLiveEvent) {
          navigate(`/${userId}/events/${eventId}/live-request${window.location.search}`, {
            replace: true,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (eventId) {
      loadEvent();
      fetchRequests(eventId);
    }
  }, [eventId]);

  useEffect(() => {
    if (user?.uid) {
      setRequesterId(user.uid);
    } else {
      signInAnonymously(auth).then((credentials) => {
        setUser(credentials.user);
        setRequesterId(credentials.user.uid);
      });
    }
  }, [user]);

  return (
    <>
      <Stack spacing={2} maxWidth="md" sx={{ mx: 'auto', p: 2, maxWidth: '600px' }}>
        <Box
          sx={{
            zIndex: (theme) => theme.zIndex.drawer - 1,
            pointerEvents: 'none',
            textAlign: 'center',
          }}
        >
          <Typography variant="h1" gutterBottom textAlign="center">
            {event?.name}
          </Typography>
          {canRequest ? (
            <Alert
              severity="success"
              variant="standard"
              icon={false}
              sx={{
                display: 'inline-flex',
                fontSize: '1rem',
                fontWeight: 'bold',
              }}
            >
              {t('requestsOpenFor', 'Requests open for')}{' '}
              {event?.expireDate && <Countdown targetDate={event.expireDate} />}
            </Alert>
          ) : (
            <Alert
              severity="error"
              variant="outlined"
              sx={(theme) => ({
                display: 'inline-flex',
                fontSize: '1rem',
                fontWeight: 'bold',
              })}
              icon={<ErrorOutlineIcon fontSize="medium" />}
            >
              {t('requestsAreClosed', 'Requests are closed')}
            </Alert>
          )}
        </Box>
        {canRequest && (
          <>
            <SearchTrack
              canRequest={canRequest}
              requests={filteredRequests}
              onRequest={handleRequest}
              onRemoveRequest={handleRemoveRequest}
              onOpenSearchResults={() => setIsOpenSearchResult(true)}
              onCloseSearchResults={() => setIsOpenSearchResult(false)}
              onPreview={handlePreview}
              onClosePreview={handleClosePreview}
            />
            <Divider sx={{ my: 2 }}>{t('or', 'or')}</Divider>
            <SpotifyImporter
              canRequest={canRequest}
              onRequest={handleRequest}
              onImportRequests={async (reqs) => {
                if (eventId) {
                  await addAnonymousRequests(eventId, reqs);
                }
              }}
              onRemoveRequest={handleRemoveRequest}
              requests={requests}
            />
          </>
        )}
      </Stack>
      <Box
        sx={{
          transition: 'all .2s',
          maxWidth: 'md',
          opacity: isOpenSearchResults ? 0.25 : 1,
          mx: 'auto',
          mb: '1rem',
        }}
      >
        {!!filteredRequests.length && (
          <Card sx={{ borderRadius: 0, mt: 3 }}>
            <CardContent>
              <Typography variant="h3">{t('requestedSongs', 'Requested songs')}</Typography>

              <Typography variant="subtitle2" color="textSecondary">
                {t('requestedSongsDesc', '{{count}} requests from you and other guests', {
                  count: filteredRequests.length,
                })}
              </Typography>

              <FormControl
                variant="filled"
                fullWidth
                sx={{
                  my: 2,
                }}
              >
                <InputLabel id="sort-requests-by">Sort by</InputLabel>
                <Select
                  labelId="sort-requests-by"
                  size="small"
                  value={requestSort}
                  onChange={(event) => setRequestSort(event.target.value as RequestSort)}
                  sx={{
                    borderRadius: 1,
                    '&::before, &::after': {
                      display: 'none',
                    },
                  }}
                >
                  <MenuItem value="date">{t('sortByDate', 'Most recent')}</MenuItem>
                  <MenuItem value="mine">{t('sortMyRequestsFirst', 'My requests first')}</MenuItem>
                  <MenuItem value="votes">{t('sortByVotes', 'Most Votes')}</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="column" gap={2} useFlexGap>
                <AnimatePresence>
                  {sortedRequests.map((request, index) => (
                    <motion.div key={request.id} layout transition={{ duration: 0.3 }}>
                      <RequestEntry
                        request={request}
                        canRequest={canRequest}
                        onRequest={handleRequest}
                        onRemoveRequest={handleRemoveRequest}
                        onPreview={handlePreview}
                        onClosePreview={handleClosePreview}
                        onChangeRequestMessage={(message) =>
                          handleChangeRequestMessage(message, request)
                        }
                        onChangeRequestPriority={(value) =>
                          handleChangeRequestPriority(value, request)
                        }
                        vip={vip}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
      {previewTrack && previewTrack.previewUrl && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            borderRadius: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pt: 2,
            pb: 1,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Box sx={{ maxWidth: 'md', mx: 'auto', px: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {previewTrack.title} - {previewTrack.artist}
            </Typography>
            <MediaPlayer
              url={previewTrack.previewUrl}
              autoplay={true}
              style={{ width: '100%' }}
              onClose={handleClosePreview}
              showCloseButton={true}
            />
          </Box>
        </Paper>
      )}
    </>
  );
}
